import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { Fish, ChevronDown, ChevronUp, FileText } from "lucide-react";
import PageMeta from "./PageMeta";

// Inline starfish SVG
const Starfish = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="currentColor" className={className}>
    <polygon points="50,2 61,35 97,35 68,57 79,91 50,70 21,91 32,57 3,35 39,35" />
  </svg>
);

const ANIMATION_STYLES = `
  @keyframes bubble-rise {
    0%   { transform: translateY(0) translateX(0) scale(1);    opacity: 0; }
    8%   { opacity: 1; }
    50%  { transform: translateY(-45vh) translateX(18px) scale(1.08); opacity: 0.85; }
    92%  { opacity: 0.5; }
    100% { transform: translateY(-95vh) translateX(-10px) scale(0.85); opacity: 0; }
  }
  @keyframes shimmer-sweep {
    0%   { background-position: -200% 0; }
    100% { background-position: 300% 0; }
  }
  @keyframes wave-drift {
    0%   { background-size: 105% auto; background-position: center 48%; }
    50%  { background-size: 110% auto; background-position: center 52%; }
    100% { background-size: 105% auto; background-position: center 48%; }
  }
`;

const BUBBLES = Array.from({ length: 30 }, (_, i) => ({
  id: i,
  left:     `${3 + ((i * 37 + 13) % 93)}%`,
  bottom:   `${(i * 11) % 25}%`,
  size:     10 + (i * 9) % 28,
  duration: 6 + (i * 2.5) % 10,
  delay:    (i * 1.1) % 10,
  opacity:  0.7 + (i % 4) * 0.08,
}));

export default function RSAASpeciesHuntPage() {
  const navigate = useNavigate();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (i) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  useEffect(() => {
    fetch("/assets/rsaa_species_hunt.xlsx")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buffer) => {
        const wb = XLSX.read(buffer, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

        if (raw.length < 2) throw new Error("Spreadsheet appears to be empty.");

        const allHdrs = raw[0].map((h) => String(h).trim());

        const keepCols = allHdrs.map((h, i) => ({ h, i }))
          .filter(({ h }) => h.toLowerCase() !== "total");
        const hdrs = keepCols.map(({ h }) => h);

        const dataRows = raw.slice(1).filter((r) => {
          const firstName = String(r[0] ?? "").trim().toLowerCase();
          if (!r.some((c) => String(c).trim() !== "")) return false;
          if (firstName === "" || firstName === "members name" || firstName === "member name" || firstName === "member's name") return false;
          return true;
        });

        const parsed = dataRows.map((r) => {
          const cells = keepCols.map(({ i }) => String(r[i] ?? "").trim());
          const speciesCount = cells.slice(1).filter((c) => c !== "").length;
          return { cells, speciesCount };
        });

        parsed.sort((a, b) => b.speciesCount - a.speciesCount);

        let currentRank = 1;
        parsed.forEach((r, i) => {
          if (i > 0 && r.speciesCount < parsed[i - 1].speciesCount) currentRank = i + 1;
          r.rank = currentRank;
        });

        setHeaders(hdrs);
        setRows(parsed);
      })
      .catch((err) => setError(err.message || "Failed to load file."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filterText.trim()
    ? rows.filter((r) => r.cells[0] === filterText)
    : rows;

  const medalEmoji = (rank) => rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
  const medalCard  = (rank) =>
    rank === 1 ? "border-yellow-500/40 bg-yellow-500/10"
    : rank === 2 ? "border-slate-400/30 bg-slate-400/10"
    : "border-amber-600/30 bg-amber-700/10";
  const medalText  = (rank) =>
    rank === 1 ? "text-yellow-400 font-bold"
    : rank === 2 ? "text-slate-300 font-semibold"
    : "text-amber-400 font-semibold";

  const starfishClass = (rank) =>
    rank === 1 ? "text-yellow-400"
    : rank === 2 ? "text-slate-300"
    : rank === 3 ? "text-amber-500"
    : "text-slate-600";

  const rankCellBg = (rank) =>
    rank === 1 ? "bg-yellow-500/20 border-yellow-500/40"
    : rank === 2 ? "bg-slate-400/20 border-slate-400/40"
    : rank === 3 ? "bg-amber-700/20 border-amber-600/40"
    : "bg-transparent border-transparent";

  const podiumRows = rows.filter((r) => r.rank <= 3);

  return (
    <>
      <PageMeta
        title="RSAA Species Hunt | Redruth Sea Angling Association"
        description="Take part in the RSAA Species Hunt — track and log the different sea fish species you catch around Cornwall."
        keywords="RSAA species hunt, sea fish species Cornwall, species challenge sea angling"
        url="https://www.redruthseaanglingassociation.co.uk/species-hunt"
      />
      <style>{ANIMATION_STYLES}</style>
      <div
        className="min-h-screen text-white"
        style={{
          backgroundImage: "url('/assets/species_hunt_bkgrd.webp')",
          backgroundSize: "105% auto",
          backgroundPosition: "center 50%",
          animation: "wave-drift 9s ease-in-out infinite",
        }}
      >
        {/* Shimmer overlay */}
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
            background: "linear-gradient(110deg, transparent 30%, rgba(180,230,255,0.07) 50%, transparent 70%)",
            backgroundSize: "200% 100%",
            animation: "shimmer-sweep 6s linear infinite",
          }}
        />

        {/* Bubbles */}
        <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 2, overflow: "hidden" }}>
          {BUBBLES.map((b) => (
            <div
              key={b.id}
              style={{
                position: "absolute",
                left: b.left,
                bottom: b.bottom,
                width: b.size,
                height: b.size,
                borderRadius: "50%",
                background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(150,220,255,0.5) 60%, rgba(100,190,255,0.15))",
                border: "2px solid rgba(255,255,255,0.75)",
                boxShadow: "0 0 6px 1px rgba(180,230,255,0.4), inset 0 0 4px rgba(255,255,255,0.3)",
                animation: `bubble-rise ${b.duration}s ease-in ${b.delay}s infinite`,
                opacity: b.opacity,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 min-h-screen bg-slate-950/75">
          <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">

            {/* Header */}
            <div className="mb-8 flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white"
              >
                ← Back
              </button>
              <div>
                <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">RSAA Species Hunt</h1>
                <p className="mt-0.5 text-sm text-slate-400">Ranked by number of species caught — most first</p>
              </div>
            </div>

            {/* Species hunt sheet & guides */}
            <a
              href="/assets/RSAA_specie_hunt_2026_sheet_with_guides.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:border-cyan-300/30 hover:bg-cyan-400/5 panel-lift"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                <FileText className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white">Download Species Hunt Card</p>
                <p className="mt-0.5 text-xs text-slate-400">Download the Species Hunt Card to join in the fun</p>
              </div>
              <span className="shrink-0 rounded-lg border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300">
                Open PDF
              </span>
            </a>

            {/* Member filter dropdown */}
            {rows.length > 0 && (
              <div className="mb-6 flex items-center gap-3">
                <label htmlFor="member-select" className="text-sm text-slate-400 whitespace-nowrap">
                  Filter by member:
                </label>
                <select
                  id="member-select"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="rounded-xl border border-white/20 bg-slate-900/80 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-300/40"
                >
                  <option value="">— All members —</option>
                  {rows.map((r, i) => (
                    <option key={i} value={r.cells[0]}>
                      {r.cells[0]}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {loading ? (
              <p className="text-cyan-200">Loading species hunt data…</p>
            ) : error ? (
              <p className="text-red-300">Error: {error}</p>
            ) : (
              <>
                {/* Podium cards */}
                {!filterText && podiumRows.length > 0 && (
                  <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {podiumRows.map((r, i) => (
                      <div key={i} className={`rounded-2xl border p-5 ${medalCard(r.rank)}`}>
                        <div className="mb-1 text-2xl">{medalEmoji(r.rank)}</div>
                        <div className={`text-lg font-bold ${medalText(r.rank)}`}>{r.cells[0] || "—"}</div>
                        <div className="mt-1 text-sm text-slate-400">{r.speciesCount} species caught</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Expandable member list */}
                <div className="rounded-2xl border border-white/10 overflow-hidden">
                  {filtered.length ? filtered.map((r, i) => {
                    const isExpanded = expandedRows.has(i);
                    const caughtSpecies = headers.slice(1).filter((_, j) => r.cells[j + 1] !== "");
                    return (
                      <div key={i} className={`border-b border-white/5 last:border-b-0`}>
                        <button
                          type="button"
                          onClick={() => toggleRow(i)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition hover:bg-white/5 ${
                            i % 2 === 0 ? "bg-slate-950/40" : "bg-slate-900/30"
                          }`}
                        >
                          {/* Rank icon */}
                          <span className={`flex-shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full border ${rankCellBg(r.rank)}`}>
                            <Starfish size={15} className={starfishClass(r.rank)} />
                          </span>

                          {/* Rank number */}
                          <span className="flex-shrink-0 w-6 text-xs text-slate-500 font-mono">
                            {r.rank}
                          </span>

                          {/* Name */}
                          <span className="flex-1 font-semibold text-white truncate">
                            {r.cells[0] || "—"}
                          </span>

                          {/* Total badge */}
                          <span className="flex-shrink-0 inline-flex items-center rounded-lg bg-emerald-500/15 px-2.5 py-0.5 text-sm font-bold text-emerald-300">
                            {r.speciesCount}
                          </span>

                          {/* Chevron */}
                          {isExpanded
                            ? <ChevronUp size={16} className="flex-shrink-0 text-slate-400" />
                            : <ChevronDown size={16} className="flex-shrink-0 text-slate-400" />
                          }
                        </button>

                        {/* Expanded species list */}
                        {isExpanded && (
                          <div className={`px-4 py-3 border-t border-white/5 ${i % 2 === 0 ? "bg-slate-950/60" : "bg-slate-900/50"}`}>
                            {caughtSpecies.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {caughtSpecies.map((species, j) => (
                                  <span
                                    key={j}
                                    className="inline-flex items-center gap-1 rounded-lg bg-slate-700/60 border border-white/10 px-2.5 py-1 text-xs text-slate-200"
                                  >
                                    <Fish size={11} className="text-emerald-400" />
                                    {species}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-500">No species recorded.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    <div className="px-4 py-6 text-center text-slate-400">
                      No results match your search.
                    </div>
                  )}
                </div>

                <p className="mt-4 text-xs text-slate-500">
                  {rows.length} member{rows.length !== 1 ? "s" : ""} · sorted by species count descending
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
